// PRD 06 §2.1 / §3.2 / §6 — popover anchored to the `+ 항목 추가` button. Lists
// only items eligible to add: matching the module's freqCat + the current
// disease scope AND not yet assigned to ANY module (미배치 only, ADM.006).
// Each row shows exactly 항목명 · N문항 · 필수/선택 (ADM.005). Closes on
// outside click or after a successful selection.

import { useEffect, useRef, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import { useItemPoolQuery } from '../hooks/use-deployment-current-config';
import type { Module, ProItemRef } from '../types/pro-deployment.types';

interface AddItemPopoverProps {
  module: Module;
  diseaseFilter: string;
  anchorRef: RefObject<HTMLElement>;
  onSelect: (item: ProItemRef) => void;
  onClose: () => void;
}

export function AddItemPopover({
  module,
  diseaseFilter,
  anchorRef,
  onSelect,
  onClose,
}: AddItemPopoverProps) {
  const { t } = useTranslation('pro-deployment');
  const popoverRef = useRef<HTMLDivElement>(null);

  // ADM.006 — the pool is 미배치-only (items assigned to ANY module are
  // excluded), so no module-exclusion param is passed.
  const poolQuery = useItemPoolQuery({
    disease: diseaseFilter,
    freq: module.frequency,
    // SHCT-516 — match the module's full cadence (frequency + weekday), so a
    // wrong-weekday item (e.g. Weekly/Sat) isn't offered for this module.
    weekdays: module.weekdays,
  });

  // Close on outside click + Escape.
  useEffect(() => {
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (popoverRef.current?.contains(target)) return;
      if (anchorRef.current?.contains(target)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [anchorRef, onClose]);

  const items = poolQuery.data ?? [];

  return (
    <div
      ref={popoverRef}
      role="listbox"
      style={{
        position: 'absolute',
        zIndex: 100,
        left: 0,
        top: 'calc(100% + 4px)',
        background: 'var(--card)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
        minWidth: 300,
        maxHeight: 240,
        overflowY: 'auto',
        padding: 'var(--space-1)',
      }}
    >
      {poolQuery.isLoading ? (
        <div
          style={{
            padding: 'var(--space-3) var(--space-4)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--muted-foreground)',
            textAlign: 'center',
          }}
        >
          {t('addItem.popover.loading')}
        </div>
      ) : items.length === 0 ? (
        <div
          style={{
            padding: 'var(--space-3) var(--space-4)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--muted-foreground)',
            textAlign: 'center',
          }}
        >
          {t('addItem.popover.empty')}
        </div>
      ) : (
        items.map((item) => {
          // ADM.005 — exactly three fields: name · question count · 필수/선택.
          const meta =
            t('addItem.popover.questionsSuffix', { count: item.questionCount }) +
            ' · ' +
            (item.required
              ? t('addItem.popover.requiredTag')
              : t('addItem.popover.optionalTag'));
          return (
            <button
              key={item.pid}
              type="button"
              role="option"
              aria-selected={false}
              onClick={() => onSelect(item)}
              style={{
                display: 'flex',
                width: '100%',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: 'var(--space-2) var(--space-3)',
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                borderRadius: 'var(--radius-sm)',
                fontSize: 'var(--font-size-sm)',
                gap: 'var(--space-3)',
                textAlign: 'left',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = 'var(--accent)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
              }}
            >
              <span>{item.name}</span>
              <span
                style={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--muted-foreground)',
                }}
              >
                {meta}
              </span>
            </button>
          );
        })
      )}
    </div>
  );
}
